import { IconButton, Tooltip, type TooltipProps } from '@mui/material';
import { type ReactElement, type ReactNode, useRef } from 'react';
import { useRecoilState } from 'recoil';
import { useToolTrayOpen } from '~/components/toolbar/shared/ToolTrayPresenceProvider';
import {
    type FloatingPanelId,
    openFloatingPanelState,
} from '~/state/atoms/toolsAtoms';

interface ToolbarButtonProps {
    icon: ReactElement;
    /**
     * Shown on hover and keyboard focus, except while a tool tray is open in
     * the enclosing {@link ToolTrayPresenceProvider} scope: the tray hangs
     * under the buttons, where the tooltip would cover it. Hiding it leaves
     * the button's `aria-label` as it is.
     */
    tooltip: ReactNode;
    /**
     * Which way the tooltip opens. Rail buttons sit closer together than the
     * tooltip's own offset, so a sideways tooltip lands on the neighbour. The
     * step rail sits in the app header at the top of the window, so below is
     * its only free side. A rail running down a pane edge passes the
     * direction off it.
     */
    tooltipPlacement?: TooltipProps['placement'];
    selected?: boolean;
    /**
     * Momentary highlight acknowledging that the button's action just ran from
     * a keyboard shortcut. Styled like {@link selected} but semantically
     * distinct — it says "this just happened", not "this tool is active" — and
     * carries no ARIA pressed state.
     */
    flashing?: boolean;
    disabled?: boolean;
    panelId?: FloatingPanelId;
    onClick?: () => void;
    anchorRef?: React.MutableRefObject<HTMLElement | null>;
    /**
     * Accessible name, for callers whose tooltip is a sentence rather than
     * the control's name (or is a node). Without it such a button announces
     * its whole tooltip, or — for a node tooltip — nothing at all. Defaults
     * to the tooltip when that is already a plain string.
     */
    ariaLabel?: string;
    ariaPressed?: boolean;
    'data-testid'?: string;
}

export default function ToolbarButton({
    icon,
    tooltip,
    tooltipPlacement = 'bottom',
    selected = false,
    flashing = false,
    disabled = false,
    panelId,
    onClick,
    anchorRef,
    ariaLabel,
    ariaPressed,
    'data-testid': testId,
}: ToolbarButtonProps) {
    const internalRef = useRef<HTMLButtonElement | null>(null);
    const [openPanel, setOpenPanel] = useRecoilState(openFloatingPanelState);
    const trayOpen = useToolTrayOpen();

    const handleClick = () => {
        if (panelId) {
            setOpenPanel((prev) => (prev === panelId ? null : panelId));
        }
        onClick?.();
    };

    const setRef = (el: HTMLButtonElement | null) => {
        internalRef.current = el;
        if (anchorRef) {
            anchorRef.current = el;
        }
    };

    const isPanelOpen = panelId !== undefined && openPanel === panelId;
    const highlighted = selected || isPanelOpen || flashing;

    return (
        <Tooltip
            // An empty title keeps the tooltip shut, and closes one already
            // showing when a tray opens under the pointer.
            title={trayOpen ? '' : tooltip}
            placement={tooltipPlacement}
        >
            <span>
                <IconButton
                    ref={setRef}
                    size="small"
                    disabled={disabled}
                    onClick={handleClick}
                    data-testid={testId}
                    aria-label={
                        ariaLabel ??
                        (typeof tooltip === 'string' ? tooltip : undefined)
                    }
                    aria-pressed={ariaPressed}
                    data-flashing={flashing ? 'true' : undefined}
                    sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        color: highlighted
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        backgroundColor: highlighted
                            ? 'rgba(25, 118, 210, 0.12)'
                            : 'transparent',
                        border: highlighted
                            ? `1px solid ${theme.palette.primary.main}`
                            : '1px solid transparent',
                        padding: '4px',
                        '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            borderColor: theme.palette.primary.main,
                        },
                        '& svg': {
                            width: '100%',
                            height: '100%',
                            fontSize: 'inherit',
                        },
                    })}
                >
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );
}
